import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useLocalStorage, useDebouncedCallback } from "@mantine/hooks";
import AssetService from "../services/AssetService";
import { AuthContext } from "./AuthContext";

export const AssetsContext = createContext(null);

export const AssetsProvider = ({
  children,
  standard: standardProp,
  organisation: organisationProp,
  pipeType,
  enableSearchParams = true,
}) => {
  const { dataScopeOrgId, orgStandard } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const currentPath = location.pathname;

  const defaultPageSize = 25;
  const paramPageIndex = parseInt(searchParams.get("pageIndex"), 10);
  const paramPageSize = parseInt(searchParams.get("pageSize"), 10);

  const getStoredPageSize = () => {
    try {
      const stored = localStorage.getItem("assets-page-size");
      const parsed = parseInt(stored, 10);
      return Number.isNaN(parsed) ? defaultPageSize : parsed;
    } catch {
      return defaultPageSize;
    }
  };

  const initialPageIndex = Number.isNaN(paramPageIndex) ? 0 : paramPageIndex;
  const initialPageSize = !Number.isNaN(paramPageSize)
    ? paramPageSize
    : getStoredPageSize();

  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });

  // Persist pageSize to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem("assets-page-size", String(pagination.pageSize));
    } catch {
      // Ignore localStorage errors
    }
  }, [pagination.pageSize]);

  const initialSearchTerm = searchParams.get("search") ?? "";
  const [search, setSearch] = useState(initialSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearchTerm);

  const debouncedSetSearch = useDebouncedCallback((value) => {
    setDebouncedSearch(value);
  }, 500);

  const handleSetSearch = useCallback(
    (value) => {
      setSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const hasMounted = useRef(false);

  useEffect(() => {
    const params = {
      pageIndex: String(pagination.pageIndex),
      pageSize: String(pagination.pageSize),
    };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (currentPath === "/assets" && enableSearchParams) {
      setSearchParams(params, { replace: true });
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    setSearchParams,
    currentPath,
    enableSearchParams,
  ]);

  const updatePagination = useCallback((updater) => {
    setPagination((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );
  }, []);

  const [assets, setAssets] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const hasDataRef = useRef(false);
  const [sorting, setSorting] = useLocalStorage({
    key: "assets-sorting",
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const [columnPinning, setColumnPinning] = useLocalStorage({
    key: "assets-column-pinning",
    defaultValue: { left: [], right: ["mrt-row-actions"] },
    getInitialValueInEffect: false,
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorage({
    key: "assets-column-visibility",
    defaultValue: {},
    getInitialValueInEffect: false,
  });
  const [columnOrder, setColumnOrder] = useLocalStorage({
    key: "assets-column-order",
    defaultValue: [],
    getInitialValueInEffect: false,
  });

  const [storedStandard, setStoredStandard] = useLocalStorage({
    key: "assets-standard",
    defaultValue: String(orgStandard || "1"),
    getInitialValueInEffect: false,
  });
  const finalStandard =
    standardProp !== undefined ? standardProp : storedStandard;
  const finalOrganisationId =
    organisationProp !== undefined ? organisationProp : dataScopeOrgId;

  // Keep the asset standard in sync with the data-scope org. Each org has its
  // own data standard, so switching the Working-In org (orgStandard tracks it)
  // resets the filter to that org's standard. Skipped when a standard is passed
  // explicitly (e.g. the inspection asset panel).
  useEffect(() => {
    if (standardProp !== undefined) return;
    if (orgStandard) setStoredStandard(String(orgStandard));
  }, [standardProp, orgStandard, setStoredStandard]);

  const memoizedSorting = useMemo(() => sorting, [JSON.stringify(sorting)]);

  // Reset pageIndex synchronously when the org changes (before any fetch fires),
  // so a high page from a previous org doesn't trigger an out-of-range request
  // (the backend 404s "Invalid Page").
  const [prevOrgId, setPrevOrgId] = useState(finalOrganisationId);
  if (finalOrganisationId !== prevOrgId) {
    setPrevOrgId(finalOrganisationId);
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    updatePagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [debouncedSearch, updatePagination]);

  const fetchAssets = useCallback(async () => {
    if (!finalOrganisationId) return;
    if (hasDataRef.current) setIsRefetching(true);
    else setLoading(true);

    try {
      const { assets: data, count: cnt } = await AssetService.getAssets({
        organisation_id: finalOrganisationId,
        standard: finalStandard,
        page_size: pagination.pageSize,
        page: pagination.pageIndex + 1,
        search: debouncedSearch,
        ordering: memoizedSorting.length
          ? `${memoizedSorting[0].desc ? "-" : ""}${memoizedSorting[0].id}`
          : "",
        pipe_type: pipeType,
      });
      // Unlike the users/inspections endpoints (which 404 on an out-of-range
      // page), the assets endpoint returns 200 with empty results. If we
      // overshot a non-empty set (stale page for an org with fewer assets),
      // snap back to the first page, which refetches.
      if (data.length === 0 && cnt > 0 && pagination.pageIndex > 0) {
        updatePagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      setAssets(data);
      setCount(cnt);
      hasDataRef.current = data.length > 0;
    } catch (error) {
      // Defensive: if the endpoint ever 404s an out-of-range page, snap back.
      if (error?.response?.status === 404 && pagination.pageIndex > 0) {
        updatePagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      console.error("Error fetching assets:", error);
      setAssets([]);
      setCount(0);
      hasDataRef.current = false;
    } finally {
      setLoading(false);
      setIsRefetching(false);
    }
  }, [
    finalOrganisationId,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    memoizedSorting,
    finalStandard,
    pipeType,
  ]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  return (
    <AssetsContext.Provider
      value={{
        assets,
        count,
        loading,
        pagination,
        setPagination: updatePagination,
        sorting,
        setSorting,
        search,
        setSearch: handleSetSearch,
        standard: finalStandard,
        setStandard: setStoredStandard,
        fetchAssets,
        columnPinning,
        setColumnPinning,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        isRefetching,
      }}
    >
      {children}
    </AssetsContext.Provider>
  );
};
